import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Input, LoadingSkeleton, Badge } from '@nexora/design-system';
import {
  useBranches,
  useCreateBranch,
  useUpdateBranch,
  type Branch,
  type BranchInput,
} from './useOrganization';

export function BranchesPage(): JSX.Element {
  const [companyId, setCompanyId] = useState('');
  const [companyInput, setCompanyInput] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | undefined>(undefined);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data: branches, isLoading, error } = useBranches(companyId);
  const createMutation = useCreateBranch();
  const updateMutation = useUpdateBranch();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleLoad = useCallback(() => {
    setCompanyId(companyInput.trim());
  }, [companyInput]);

  const handleCreate = useCallback(
    async (input: BranchInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Filial criada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao criar filial.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: BranchInput) => {
      if (!editingBranch) return;
      try {
        await updateMutation.mutateAsync({ id: editingBranch.id, ...input });
        showToast('Filial atualizada com sucesso.', 'success');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro ao atualizar filial.';
        showToast(msg, 'error');
        throw err;
      }
    },
    [editingBranch, updateMutation, showToast],
  );

  const handleEdit = useCallback((branch: Branch) => {
    setEditingBranch(branch);
    setModalOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setEditingBranch(undefined);
    setModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback((id: string) => {
    setDeleteConfirmId(id);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmId(null);
  }, []);

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
        }}
      >
        <h1 className="font-display-lg text-display-lg text-on-surface m-0">
          Filiais
          {branches && (
            <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
              ({branches.length})
            </span>
          )}
        </h1>
        {companyId && (
          <Button variant="primary" onClick={handleNew}>
            + Nova Filial
          </Button>
        )}
      </div>

      {/* Company ID filter */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '300px', flex: 1 }}>
            <Input
              label="ID da Empresa"
              value={companyInput}
              onChange={(e) => setCompanyInput(e.target.value)}
              placeholder="Ex: uuid-da-empresa"
            />
          </div>
          <Button variant="primary" onClick={handleLoad} disabled={!companyInput.trim()}>
            Carregar
          </Button>
        </div>
      </Card>

      {/* Content */}
      {companyId && (
        <Card variant="outlined" padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {branches?.length ?? 0} {(branches?.length ?? 0) === 1 ? 'filial' : 'filiais'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Filiais</h3>
          </CardHeader>
          {/* Loading */}
          {isLoading && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem',
                gap: '0.75rem',
                color: '#43474e',
              }}
            >
              <LoadingSkeleton />
              <span style={{ fontSize: '0.875rem' }}>Carregando filiais...</span>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div
              style={{
                padding: '3rem 2rem',
                textAlign: 'center',
                color: '#ba1a1a',
              }}
            >
              <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar filiais</p>
              <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
                {error instanceof Error ? error.message : 'Erro desconhecido'}
              </p>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && branches && branches.length === 0 && (
            <div
              style={{
                padding: '3rem 2rem',
                textAlign: 'center',
                color: '#74777f',
              }}
            >
              <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
                Nenhuma filial encontrada para esta empresa.
              </p>
              <Button variant="outline" size="sm" onClick={handleNew}>
                Criar primeira filial
              </Button>
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && branches && branches.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.875rem',
                }}
              >
                <thead>
                  <tr>
                    {['Nome', 'Empresa', 'Criada em', 'Ações'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: '0.75rem 1rem',
                          background: '#f2f4f6',
                          color: '#43474e',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          borderBottom: '2px solid #e0e3e5',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {branches.map((branch) => (
                    <tr
                      key={branch.id}
                      style={{ transition: 'background 0.15s ease' }}
                      className="org-table-row"
                    >
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600 }}>
                        {branch.name}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                        <Badge variant="info" size="sm">{branch.companyId.slice(0, 8)}...</Badge>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                        {new Date(branch.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(branch)}>
                            Editar
                          </Button>
                          {deleteConfirmId === branch.id ? (
                            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.75rem', color: '#ba1a1a' }}>Excluir?</span>
                              <Button variant="danger" size="sm" onClick={() => setDeleteConfirmId(null)}>
                                Sim
                              </Button>
                              <Button variant="ghost" size="sm" onClick={handleDeleteCancel}>
                                Não
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteConfirm(branch.id)}
                              style={{ color: '#ba1a1a' }}
                            >
                              Excluir
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Company not selected */}
      {!companyId && (
        <Card padding="2rem" variant="outlined">
          <p style={{ textAlign: 'center', color: '#74777f', fontSize: '0.875rem', margin: 0 }}>
            Informe o ID da empresa acima para carregar as filiais.
          </p>
        </Card>
      )}

      {/* Branch form modal */}
      <BranchFormModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingBranch(undefined); }}
        branch={editingBranch}
        companyId={companyId}
        onSubmit={editingBranch ? handleUpdate : handleCreate}
      />

      {/* Toast */}
      {toastMessage && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            right: '1.5rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6',
            border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`,
            color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 10000,
            maxWidth: '400px',
            animation: 'toast-slide-in 0.3s ease',
          }}
        >
          {toastMessage}
          <button
            onClick={() => setToastMessage(null)}
            aria-label="Fechar"
            style={{
              marginLeft: '0.75rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              fontSize: '1rem',
              padding: '0',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      )}

      <style>{`
        .org-table-row:hover td {
          background: #eceef0;
        }
        .filter-card { margin-bottom: 1rem; }
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ── Branch Form Modal ── */

interface BranchFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  branch?: Branch;
  companyId: string;
  onSubmit: (data: BranchInput) => Promise<void>;
}

function BranchFormModal({ isOpen, onClose, branch, companyId, onSubmit }: BranchFormModalProps): JSX.Element {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (branch) {
      setName(branch.name);
    } else {
      setName('');
    }
    setError(null);
  }, [branch, isOpen]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Nome da filial e obrigatório.');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({ name: name.trim(), companyId });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar filial.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = branch ? 'Editar Filial' : 'Nova Filial';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: isOpen ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        }}
      >
        <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.125rem', fontWeight: 700, color: '#191c1e' }}>
          {title}
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Input
              label="Nome da Filial"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Matriz"
              required
            />

            {error && (
              <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>
                {error}
              </p>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e0e3e5',
            }}
          >
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={submitting}>
              Salvar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
